/**
 * The Candidate Requests a feedback report can be filed against.
 *
 * Read from `requestsCache` — the queue the tab already loaded for THIS mailbox, in memory — so
 * opening the panel costs no Gmail, Lever or model call. That cache is owner-checked, which is
 * what stops the dropdown ever listing another recruiter's mail: `?email=` selects whose BOARD you
 * are viewing, never whose inbox, and this must keep following the inbox.
 *
 * Empty is a normal state (the tab has not been opened this session, or the queue is clear); the
 * panel says so rather than pretending the feature is broken.
 */
import { readRequestsCache } from '../views/taActionBoard/requestsCache';
import type { InboundRequest } from '../types/taActionBoard';

export interface RequestOption {
  threadId: string;
  /** What the dropdown row reads. */
  label: string;
  /**
   * Everything typing can match, pre-lowercased: name, ADDRESS and subject.
   *
   * The address is searchable even though it is not in the label — a recruiter chasing a specific
   * thread often has the address to hand from Gmail and not the exact spelling of the name.
   */
  search: string;
  request: InboundRequest;
}

/** What actually travels to Slack — assembled here so the panel never hand-picks PII fields. */
export interface RequestContext {
  candidateName: string;
  candidateEmail: string;
  question: string;
  draft: string;
  draftSource: string;
  categoryLabel: string;
  leverUrl: string;
  gmailUrl: string;
}

const trim = (value: string, max: number) => (value.length > max ? `${value.slice(0, max)}…` : value);

export function listRequestOptions(ownerEmail: string): RequestOption[] {
  const items = readRequestsCache(ownerEmail);
  if (!items?.length) return [];

  return items
    .slice()
    // Newest first: the request someone is complaining about is almost always the one they were
    // just looking at.
    .sort((a, b) => `${b.receivedAt}`.localeCompare(`${a.receivedAt}`))
    .map((request) => ({
      threadId: request.threadId,
      label: trim(
        [request.candidateName || request.candidateEmail || 'Unknown', request.subject]
          .filter(Boolean)
          .join(' — '),
        90
      ),
      search: [request.candidateName, request.candidateEmail, request.subject]
        .filter(Boolean)
        .join(' ')
        .toLowerCase(),
      request,
    }));
}

/**
 * Filter the picker as someone types.
 *
 * Every whitespace-separated term must match somewhere (AND, not OR), so "anna case" narrows
 * rather than widening to everything containing either word — with 40 threads in a queue, an OR
 * search returns the whole list and reads as broken. Substring, not prefix: people search for a
 * surname or a domain, not the start of the label.
 */
export function filterRequestOptions(options: RequestOption[], query: string): RequestOption[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return options;
  return options.filter((option) => terms.every((term) => option.search.includes(term)));
}

/**
 * The untruncated question, and the draft as the recruiter saw it.
 *
 * `questionFull` rather than `question`: the collapsed card's copy is cut for layout, and a report
 * about a misread question is unanswerable if the reader only sees the same cut text.
 */
export function toRequestContext(request: InboundRequest): RequestContext {
  return {
    candidateName: request.candidateName ?? '',
    candidateEmail: request.candidateEmail ?? '',
    question: request.questionFull || request.question || '',
    draft: request.draft ?? '',
    draftSource: request.draftSource ?? '',
    categoryLabel: request.categoryLabel ?? '',
    leverUrl: request.leverUrl ?? '',
    gmailUrl: request.gmailUrl ?? '',
  };
}
